<template>
  <div v-if="!showEditModal" class="team-modal-overlay">
    <div class="team-modal-content" :class="{'tm-timer-bg': timerActive}">
      <template v-if="!timerActive">
        <!-- HEADER -->
        <div class="tm-header">
          <div class="tm-header-left">
            <div class="tm-icon-box"><i class="fa fa-gamepad"></i></div>
            <div class="tm-header-text">
              <div class="tm-title">START GAME</div>
              <div class="tm-subtitle">CONFIGURE GAME MODE</div>
            </div>
          </div>
          <div class="tm-close" @click="closeModal"><i class="fa fa-times"></i></div>
        </div>

        <!-- MAIN CONTENT AREA -->
        <div class="tm-body">
          <div class="tm-section-header">
            <span class="tm-bar"></span> TEAM PREVIEW
            <div class="tm-teams-count">{{ teams.length }} TEAMS</div>
          </div>

          <div class="tm-teams-list">
            <div v-for="team in teams" :key="team.id" class="tm-team-card" :style="{borderColor: team.color, cursor: 'pointer'}" @click="toggleTeam(team.id)">
              <div class="tm-team-card-main">
                <div class="tm-team-title" :style="{color: team.color}">{{ team.name }}</div>
                <div class="tm-team-members-preview" v-if="!expandedTeams.includes(team.id)">
                  <template v-for="(member, idx) in team.members.slice(0, 3)">
                    <div :key="member.id + '-img'" class="tm-avatar-img" :style="{backgroundImage: `url(${member.avatar})`}" v-if="member.avatar"></div>
                    <div :key="member.id + '-txt'" class="tm-avatar" :style="{backgroundColor: getContrastColor(member.colorIndex)}" v-else>
                      {{ getInitials(member.name) }}
                    </div>
                  </template>
                  <div v-if="team.members.length > 3" class="tm-avatar tm-more" style="background-color: #0f3c44; color: #00ffff">
                    +{{ team.members.length - 3 }}
                  </div>
                </div>
                <div class="tm-dropdown-icon">
                  <i class="fa" :class="expandedTeams.includes(team.id) ? 'fa-caret-up' : 'fa-caret-down'" :style="{color: team.color}"></i>
                </div>
              </div>

              <!-- EXPANDED MEMBERS -->
              <div v-if="expandedTeams.includes(team.id)" class="tm-team-expanded">
                <div v-for="member in team.members" :key="member.id" class="tm-member-row tm-member-preview-row">
                  <div class="tm-member-info">
                    <div class="tm-avatar-img" :style="{backgroundImage: `url(${member.avatar})`}" v-if="member.avatar"></div>
                    <div class="tm-avatar" :style="{backgroundColor: getContrastColor(member.colorIndex)}" v-else>{{ getInitials(member.name) }}</div>
                    <span class="tm-member-name">{{ member.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- FIXED FOOTER -->
        <div class="tm-footer-actions tm-footer-preview">
          <span></span>
          <button class="tm-btn-primary" @click="startGame">START GAME <i class="fa fa-play" style="font-size: 10px; margin-left: 4px"></i></button>
        </div>
      </template>

      <!-- TIMER UI -->
      <template v-else>
        <div class="tm-timer-container">
          <div class="tm-timer-badge">GAME ON STARTING</div>
          <div class="tm-timer-subtitle">You are going to interactive Game Mode in ...</div>
          <div class="tm-timer-box">
            <div class="tm-timer-number">{{ timerCount }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <TeamEditModal v-else :initialTeams="teams" :coreColors="coreColors" @cancel="showEditModal = false" @save="handleSaveTeams" />
</template>

<script>
import TeamEditModal from './TeamEditModal.vue'

export default {
  name: 'TeamSelectionModal',
  components: {
    TeamEditModal,
  },
  props: {
    gameConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showEditModal: false,
      timerActive: false,
      timerCount: 5,
      teams: [],
      expandedTeams: [],
      coreColors: [
        '#FF3366',
        '#FF9933',
        '#FFCC00',
        '#33CC33',
        '#3399FF',
        '#9933FF',
        '#FF66B2',
        '#FFB266',
        '#FFE566',
        '#66E566',
        '#66B2FF',
        '#B266FF',
        '#FF0000',
        '#FF8000',
        '#FFFF00',
        '#00FF00',
        '#0000FF',
        '#8000FF',
        '#CC0000',
        '#CC6600',
        '#CCCC00',
        '#00CC00',
        '#0000CC',
        '#6600CC',
        '#FF4D4D',
        '#FFA64D',
        '#FFFF4D',
        '#4DFF4D',
        '#4D4DFF',
        '#A64DFF',
      ],
    }
  },
  mounted() {
    this.parseGameOnData()
  },
  methods: {
    parseGameOnData() {
      if (!this.gameConfig || !this.gameConfig.gameOnRecord) {
        // this.initDummyData();
        return
      }
      const record = this.gameConfig.gameOnRecord
      const dbTeams = record.teams || []
      const dbMembers = record.members || []
      const classMembers =
        (this.$store && this.$store.state && this.$store.state.teacher && this.$store.state.teacher.rooms && this.$store.state.teacher.rooms.members) || []

      let cIdx = 0
      this.teams = dbTeams.map((t, idx) => {
        const teamColor = this.coreColors[idx % this.coreColors.length]
        const teamMembers = dbMembers
          .filter((m) => m.teamId === t.id)
          .map((m) => {
            const user = classMembers.find((c) => c._id === m.uid) || {}
            let mName = typeof m.name === 'string' ? m.name : Array.isArray(m.name) ? m.name.join(' ') : null
            return {
              id: m.uid,
              name: mName || user.nickname || user.name || 'Unknown User',
              avatar: m.avatar || user.avatar || user.pic || '',
              score: m.score || 0,
              originalMemberData: m, // Preserve original backend fields just in case
              colorIndex: cIdx++,
            }
          })

        return {
          id: t.id,
          name: t.name || `TEAM ${t.id}`,
          score: t.score || 0,
          progress: t.progress || 0,
          color: teamColor,
          members: teamMembers,
        }
      })
    },
    toggleTeam(teamId) {
      if (this.expandedTeams.includes(teamId)) {
        this.expandedTeams = this.expandedTeams.filter((id) => id !== teamId)
      } else {
        this.expandedTeams.push(teamId)
      }
    },
    closeModal() {
      this.$emit('close')
    },
    startGame() {
      if (this.timerActive) return
      this.timerActive = true
      this.timerCount = 5

      // Pass teams and the record id to GameTrackingPage
      const finalPayload = {
        _id: this.gameConfig.gameOnRecord._id,
        teams: this.teams, // enriched local teams (with name, color, members w/ names)
        timeRemaining: this.gameConfig.gameOnRecord.expiresAt,
      }

      if (window.App && this.gameConfig.gameOnRecord && this.gameConfig.gameOnRecord._id) {
        window.App.service('game-on')
          .patch(this.gameConfig.gameOnRecord._id, {
            status: 1,
          })
          .catch((err) => {
            console.error('Failed to patch game-on status:', err)
            return
          })
      }

      // BROADCAST REAL-TIME START TO STUDENTS WHEN TIMER INITIATES
      if (window.App) {
        window.App.service('auth').patch('syncTeacher', {
          commit: 'student/startGameOn',
          data: finalPayload,
        })
      }

      const interval = setInterval(() => {
        this.timerCount--
        if (this.timerCount === 0) {
          clearInterval(interval)
          this.timerActive = false
          this.$emit('start', finalPayload)
        }
      }, 1000)
    },
    handleSaveTeams(newTeams) {
      if (this.gameConfig && this.gameConfig.gameOnRecord && this.gameConfig.gameOnRecord._id) {
        const dbMembers = []
        const dbTeams = []

        newTeams.forEach((team) => {
          dbTeams.push({
            id: team.id,
            name: team.name,
            score: team.score || 0,
            progress: team.progress || 0,
          })

          team.members.forEach((m) => {
            const memberObj = {teamId: team.id, uid: m.id, score: m.score || 0}
            // Preserve any extra backend tags
            if (m.originalMemberData) {
              Object.assign(memberObj, m.originalMemberData, {teamId: team.id})
            }
            dbMembers.push(memberObj)
          })
        })

        if (window.App) {
          // Patch the full synchronized arrays to match DB schema
          window.App.service('game-on')
            .patch(this.gameConfig.gameOnRecord._id, {
              teams: dbTeams,
              members: dbMembers,
            })
            .catch((err) => console.error('Failed to patch game-on members:', err))
        }
      }

      this.teams = newTeams
      this.showEditModal = false
    },
    patchGameOnMove(playerId, fromTeamId, toTeamId) {
      if (window.App && this.gameConfig && this.gameConfig.gameOnRecord) {
        return window.App.service('game-on')
          .patch(this.gameConfig.gameOnRecord._id, {
            action: 'move',
            uid: playerId,
            fromTeam: fromTeamId,
            toTeam: toTeamId,
          })
          .catch((err) => console.error(err))
      }
    },
    patchGameOnSwitch(uid1, team1, uid2, team2) {
      if (window.App && this.gameConfig && this.gameConfig.gameOnRecord) {
        return window.App.service('game-on')
          .patch(this.gameConfig.gameOnRecord._id, {
            action: 'switch',
            members: [
              {uid: uid1, teamId: team1},
              {uid: uid2, teamId: team2},
            ],
          })
          .catch((err) => console.error(err))
      }
    },
    getInitials(name) {
      if (!name) return ''
      const parts = name.split(' ')
      if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    getContrastColor(index) {
      const idx = index || 0
      const cycle = Math.floor(idx / 30)
      let base = this.coreColors[idx % 30] || '#808080'
      if (cycle > 0) {
        const dimFactor = Math.max(0.1, 1 - cycle * 0.1)
        base = this.dimHex(base, dimFactor)
      }
      return base
    },
    dimHex(hex, factor) {
      hex = hex.replace('#', '')
      let r = parseInt(hex.substring(0, 2), 16)
      let g = parseInt(hex.substring(2, 4), 16)
      let b = parseInt(hex.substring(4, 6), 16)
      r = Math.floor(r * factor)
      g = Math.floor(g * factor)
      b = Math.floor(b * factor)
      return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')
    },
  },
}
</script>

<style scoped>
.team-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.team-modal-content {
  width: 800px;
  max-height: 90vh;
  min-height: 500px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  font-family: Arial, sans-serif;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.tm-timer-bg {
  background-image: linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url('~@/assets/picture/gameOnTimer.png');
  background-size: cover;
  background-position: center;
  border: 2px solid rgba(16, 185, 129, 0.7);
  box-shadow: 0 0 30px rgba(16, 185, 129, 0.15);
}

.tm-timer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
}
.tm-timer-badge {
  border: 1px solid #10b981;
  border-radius: 20px;
  padding: 6px 20px;
  color: #10b981;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 2px;
}
.tm-timer-subtitle {
  color: #64748b;
  font-size: 14px;
  margin-top: 24px;
  margin-bottom: 40px;
  border: none;
}
.tm-timer-box {
  width: 320px;
  height: 160px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #f8fafc;
}
.tm-timer-box::before {
  content: '';
  position: absolute;
  top: -12px;
  left: -12px;
  right: -12px;
  bottom: -12px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 20px;
}
.tm-timer-number {
  color: #10b981;
  font-size: 100px;
  font-weight: bold;
  text-shadow: none;
  line-height: 1;
}

/* HEADER */
.tm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.tm-header-left {
  display: flex;
  align-items: center;
}

.tm-icon-box {
  background: transparent;
  padding: 6px 0;
  color: #10b981;
  font-size: 24px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tm-header-text {
  display: flex;
  flex-direction: column;
}

.tm-title {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}
.tm-subtitle {
  font-size: 10px;
  color: #10b981;
  letter-spacing: 0.5px;
}

.tm-close {
  cursor: pointer;
  color: #64748b;
  font-size: 20px;
  transition: 0.2s;
}
.tm-close:hover {
  color: #1e293b;
}

/* BODY */
.tm-body {
  padding: 30px 40px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tm-section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
  color: #1e293b;
}

.tm-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background-color: #10b981;
  margin-right: 8px;
}

.tm-teams-count {
  margin-left: auto;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  background-color: #f8fafc;
}

/* PREVIEW MODE */
.tm-teams-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}
.tm-team-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 4px solid;
  border-top: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.tm-team-card:hover {
  background: #f8fafc;
}

.tm-team-card-main {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
  box-sizing: border-box;
}

.tm-team-expanded {
  padding: 0 20px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tm-member-preview-row {
  border: none;
  background: #f1f5f9;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}
.tm-member-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tm-member-name {
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  color: #1e293b;
}

.tm-team-title {
  font-size: 14px;
  font-weight: bold;
  flex: 1;
  min-width: 150px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.tm-team-members-preview {
  display: flex;
  flex: 3;
  gap: -4px;
}

.tm-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: bold;
  color: #fff;
  border: 2px solid #ffffff;
  margin-left: -6px;
}
.tm-avatar:first-child {
  margin-left: 0;
}
.tm-avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 2px solid #ffffff;
  margin-left: -6px;
}
.tm-avatar-img:first-child {
  margin-left: 0;
}
.tm-avatar.tm-more {
  background-color: #cbd5e1 !important;
  color: #1e293b !important;
}

.tm-dropdown-icon {
  cursor: pointer;
  opacity: 0.8;
  font-size: 18px;
}
.tm-dropdown-icon:hover {
  opacity: 1;
}

/* FIX FOOTER */
.tm-footer-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  padding: 15px 40px;
  border-top: 1px solid #e2e8f0;
}

.tm-btn-outline {
  background: transparent;
  border: 1px solid #10b981;
  color: #10b981;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}
.tm-btn-outline:hover {
  background: #ecfdf5;
}

.tm-btn-primary {
  background: #10b981;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: 0.2s;
}
.tm-btn-primary:hover {
  background: #059669;
  transform: scale(1.02);
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f5f9;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
